// src/hooks/use-network-status.ts
import { useEffect, useState, useCallback } from 'react';
import { isNative } from '../platform';

type NetworkStatus = {
  connected: boolean;
  connectionType: string;
};

export const useNetworkStatus = (): NetworkStatus & { checkNow: () => Promise<void> } => {
  const [status, setStatus] = useState<NetworkStatus>({
    connected: true, // optimistic default to avoid flash
    connectionType: 'unknown',
  });

  const checkNow = useCallback(async () => {
    if (isNative()) {
      const { Network } = await import('@capacitor/network');
      const s = await Network.getStatus();
      setStatus({ connected: s.connected, connectionType: s.connectionType });
    } else {
      setStatus({ connected: navigator.onLine, connectionType: 'unknown' });
    }
  }, []);

  useEffect(() => {
    // Initial check
    checkNow();

    if (isNative()) {
      let listenerHandle: { remove: () => Promise<void> } | null = null;

      (async () => {
        const { Network } = await import('@capacitor/network');
        listenerHandle = await Network.addListener('networkStatusChange', (s) => {
          setStatus({ connected: s.connected, connectionType: s.connectionType });
        });
      })();

      return () => {
        listenerHandle?.remove();
      };
    } else {
      const onOnline = () => setStatus({ connected: true, connectionType: 'unknown' });
      const onOffline = () => setStatus({ connected: false, connectionType: 'unknown' });
      window.addEventListener('online', onOnline);
      window.addEventListener('offline', onOffline);
      return () => {
        window.removeEventListener('online', onOnline);
        window.removeEventListener('offline', onOffline);
      };
    }
  }, [checkNow]);

  return { ...status, checkNow };
};
